import React, { useState } from "react";
import { Link } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import LeadForm from "@/components/LeadForm";
import SampleAdsCTASection from "@/components/SampleAdsCTASection";
import { caseStudies, caseStudyList } from "@/data/caseStudies";
import {
  Globe,
  Wine,
  Utensils,
  Sparkles,
  ArrowRight,
  Quote,
  TrendingUp,
  Play,
  X,
} from "lucide-react";

const ICONS = { Globe, Wine, Utensils, Sparkles };

const VideoLightbox = ({ src, poster, label, onClose }) => (
  <div
    className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-5"
    onClick={onClose}
    data-testid="case-study-video-lightbox"
  >
    <button
      type="button"
      onClick={onClose}
      aria-label="Close video"
      className="absolute top-5 right-5 w-10 h-10 rounded-full bg-[var(--af-navy-deep)]/80 border border-[var(--af-cream)]/20 text-[var(--af-cream)] hover:text-[var(--af-lime)] hover:border-[var(--af-lime)] flex items-center justify-center transition-colors"
      data-testid="case-study-video-close"
    >
      <X size={18} />
    </button>
    <div
      className="w-full max-w-5xl aspect-video bg-black rounded-xl overflow-hidden shadow-2xl"
      onClick={(e) => e.stopPropagation()}
    >
      <video
        src={src}
        poster={poster}
        controls
        autoPlay
        playsInline
        className="w-full h-full object-contain"
        data-testid="case-study-video-player"
      >
        <track kind="captions" />
      </video>
    </div>
    {label && (
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs uppercase tracking-widest text-[var(--af-cream)]/70 font-eyebrow">
        {label}
      </div>
    )}
  </div>
);

const FeaturedCard = ({ cs }) => {
  const Icon = ICONS[cs.icon] || Globe;
  const [videoOpen, setVideoOpen] = useState(false);
  const anchorImg = cs.anchorImage || cs.image;

  return (
    <article
      className="surface p-6 sm:p-10 lg:p-12"
      data-testid={`case-study-featured-${cs.slug}`}
    >
      {/* Eyebrow + headline row */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[var(--af-lime)] flex items-center justify-center">
              <Icon size={17} className="text-[var(--af-navy)]" />
            </div>
            <div className="eyebrow !mb-0">{cs.categoryLabel}</div>
          </div>
          <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-3xl">
            {cs.headline}
          </h3>
        </div>
        <div className="font-eyebrow text-[10px] uppercase tracking-widest bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 text-[var(--af-lime)] px-3 py-1.5 rounded-full whitespace-nowrap self-start sm:self-end">
          Featured Case Study
        </div>
      </div>

      {/* Anchor image — never upscaled past native 1200px, capped smaller
          for visual balance with the surrounding text. */}
      <figure
        className="relative mx-auto mb-8 rounded-2xl overflow-hidden border border-[var(--af-lime)]/20 bg-black shadow-[0_30px_80px_-30px_rgba(0,0,0,0.6)]"
        style={{ maxWidth: "720px" }}
        data-testid="case-study-anchor-figure"
      >
        <img
          src={anchorImg}
          alt={cs.client + " — " + cs.headline}
          width="1200"
          height="628"
          className="block w-full h-auto"
          loading="lazy"
          data-testid="case-study-anchor-image"
        />
      </figure>

      {/* Info layout: summary + metrics + testimonial */}
      <div className="grid lg:grid-cols-12 gap-8 lg:gap-10">
        <div className="lg:col-span-7">
          <p className="text-base sm:text-lg text-[var(--af-cream)]/85 leading-relaxed mb-7">
            {cs.summary}
          </p>
          {cs.testimonial && (
            <div className="border-l-2 border-[var(--af-lime)] pl-5 italic text-sm sm:text-base text-[var(--af-cream)]/85 leading-relaxed">
              &ldquo;{cs.testimonial.quote}&rdquo;
              <div className="not-italic mt-3 text-[11px] text-[var(--af-cream)]/55 font-eyebrow uppercase tracking-widest">
                , {cs.testimonial.author}
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 space-y-5">
          <div className="grid grid-cols-3 gap-4">
            {cs.metrics.slice(0, 3).map((m, i) => (
              <div
                key={i}
                className="border-l-2 border-[var(--af-lime)] pl-3"
                data-testid={`case-study-featured-metric-${i}`}
              >
                <div className="font-display text-2xl sm:text-3xl text-[var(--af-lime)] leading-none">
                  {m.value}
                </div>
                <div className="text-[10px] uppercase tracking-wider opacity-65 mt-1.5">
                  {m.label}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3 pt-1">
            {cs.videoUrl && (
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                className="btn-ghost-lime"
                data-testid={`case-study-watch-${cs.slug}`}
              >
                <Play size={14} /> View Comprehensive Report
              </button>
            )}
            <a
              href="#lead-form"
              className="btn-lime"
              data-testid={`case-study-cta-${cs.slug}`}
            >
              Run a campaign like this <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>

      {videoOpen && cs.videoUrl && (
        <VideoLightbox
          src={cs.videoUrl}
          poster={anchorImg}
          label={cs.client + " · Comprehensive Campaign Report"}
          onClose={() => setVideoOpen(false)}
        />
      )}
    </article>
  );
};

const StoryCard = ({ cs }) => {
  const Icon = ICONS[cs.icon] || Globe;
  return (
    <article
      className="surface overflow-hidden flex flex-col"
      data-testid={`case-study-card-${cs.slug}`}
    >
      <div className="relative h-44 bg-gradient-to-br from-[var(--af-navy-soft)] to-[var(--af-navy-deep)] overflow-hidden">
        <img
          src={cs.image}
          alt={cs.client}
          className="absolute inset-0 w-full h-full object-cover opacity-90"
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--af-navy-deep)] via-[var(--af-navy-deep)]/30 to-transparent" />
        <div className="absolute top-4 left-4">
          <div className="w-8 h-8 rounded-full bg-[var(--af-lime)]/95 border border-[var(--af-navy)] flex items-center justify-center">
            <Icon size={15} className="text-[var(--af-navy)]" />
          </div>
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="font-display text-4xl text-[var(--af-lime)] leading-none">
            {cs.heroMetric.value}
          </div>
          <div className="text-[11px] uppercase tracking-wider text-[var(--af-cream)]/85 mt-1.5">
            {cs.heroMetric.label}
          </div>
        </div>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <div className="eyebrow mb-3 !text-[10px] !tracking-widest">
          {cs.categoryLabel}
        </div>
        <h3 className="font-display text-xl mb-3 leading-tight">
          {cs.headline}
        </h3>
        <p className="text-sm text-[var(--af-cream)]/70 mb-5 flex-1 leading-relaxed">
          {cs.summary}
        </p>
        <div className="grid grid-cols-2 gap-3 mb-5">
          {cs.metrics.slice(0, 4).map((m, i) => (
            <div key={i} className="text-xs">
              <div className="font-display text-base text-[var(--af-lime)]">
                {m.value}
              </div>
              <div className="text-[10px] uppercase tracking-wider opacity-60 mt-0.5">
                {m.label}
              </div>
            </div>
          ))}
        </div>
        <a
          href="#lead-form"
          className="text-sm font-semibold text-[var(--af-lime)] hover:underline underline-offset-4 inline-flex items-center gap-1.5 mt-auto"
          data-testid={`case-study-link-${cs.slug}`}
        >
          Run a campaign like this <ArrowRight size={14} />
        </a>
      </div>
    </article>
  );
};

const CaseStudies = () => {
  const featured = caseStudyList.find((c) => c.featured);
  const rest = caseStudyList.filter((c) => !c.featured);

  return (
    <SiteShell>
      {/* HERO */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="case-studies-hero"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24 relative z-10">
          <Link
            to="/tourism"
            className="inline-flex items-center gap-2 text-xs text-[var(--af-cream)]/55 hover:text-[var(--af-lime)] mb-8 transition-colors"
            data-testid="case-studies-back-link"
          >
            ← Back to Tourism
          </Link>
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 fade-up">
              <div className="eyebrow mb-5">Tourism · Real Campaigns · Real Proof</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-6">
                Results, already
                <br />
                <span className="text-[var(--af-lime)]">in the bank.</span>
              </h1>
              <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
                Five tourism and culinary campaigns, across DMOs, wineries,
                and experiential brands, that turned real client problems into
                real, measurable results. Every metric below is straight from
                the campaign dashboard.
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 gap-4">
              {[
                { v: "5", l: "Featured Case Studies" },
                { v: "12+ mo", l: "Avg Campaign Length" },
                { v: "2.5M+", l: "Combined Impressions" },
                { v: "70K+", l: "Engaged Visitors" },
              ].map((s, i) => (
                <div
                  key={i}
                  className="surface p-4 fade-up"
                  style={{ animationDelay: `${0.1 + i * 0.05}s` }}
                >
                  <div className="font-display text-2xl text-[var(--af-lime)] leading-none">
                    {s.v}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider opacity-65 mt-2">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section
          className="px-5 sm:px-8 py-16 max-w-7xl mx-auto"
          data-testid="case-studies-featured-section"
        >
          <div className="eyebrow mb-4">The Anchor Story</div>
          <FeaturedCard cs={featured} />
        </section>
      )}

      {/* MAUREEN KELLEY REFERENCE LETTER */}
      <section
        className="border-y border-[var(--af-line)] bg-[var(--af-navy-deep)] px-5 sm:px-8 py-20"
        data-testid="reference-letter-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">Verified Client Reference</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl mb-7 leading-[1.05]">
                A formal reference,
                <br />
                <span
                  className="relative inline-block text-[var(--af-lime)] animate-record-glow"
                  data-testid="reference-on-the-record"
                >
                  on the record.
                  <span
                    aria-hidden="true"
                    className="absolute left-0 -bottom-1 h-[3px] w-full bg-[var(--af-lime)]/70 rounded-full animate-record-underline origin-left"
                  />
                </span>
              </h2>
              <div className="surface p-7 inline-block">
                <div className="flex items-baseline gap-3 mb-3">
                  <div className="font-display text-7xl text-[var(--af-lime)] leading-none">
                    10
                  </div>
                  <div className="text-2xl text-[var(--af-cream)]/55">/ 10</div>
                </div>
                <div className="font-eyebrow text-xs uppercase tracking-widest text-[var(--af-cream)]/65">
                  Performance rating from Nelson County, VA
                </div>
                <div className="dashed-divider my-5" />
                <div className="text-sm text-[var(--af-cream)]/80 leading-relaxed">
                  Maureen Kelley, Director of Economic Development & Tourism
                  for Nelson County, Virginia, a multi-year client, provided
                  this reference in support of our work.
                </div>
              </div>
            </div>

            <div className="lg:col-span-7 space-y-5">
              <div
                className="surface p-6"
                data-testid="reference-quote-rating"
              >
                <Quote
                  size={18}
                  className="text-[var(--af-lime)] mb-3"
                />
                <p className="text-base sm:text-lg text-[var(--af-cream)]/90 italic leading-relaxed">
                  "Anne Flinn Advertising consistently delivers high-quality
                  creative products, strategic guidance, and measurable
                  results. On a scale of 1 to 10, I would rate their
                  performance a{" "}
                  <span className="text-[var(--af-lime)] not-italic font-semibold">
                    10
                  </span>
                  ."
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div
                  className="surface p-6"
                  data-testid="reference-q-rehire"
                >
                  <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                    Would you rehire?
                  </div>
                  <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                    "Absolutely. I would not hesitate to hire them again and
                    would strongly recommend them to other organizations
                    seeking strategic communications, marketing, branding, or
                    public outreach services."
                  </p>
                </div>

                <div
                  className="surface p-6"
                  data-testid="reference-q-timeline"
                >
                  <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                    Did they meet your timeline?
                  </div>
                  <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                    "Yes. Anne Flinn Advertising has consistently met project
                    deadlines and often exceeds expectations when working
                    under compressed timelines."
                  </p>
                </div>

                <div
                  className="surface p-6"
                  data-testid="reference-q-strength"
                >
                  <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                    Greatest strength?
                  </div>
                  <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                    "Their ability to understand complex issues, translate
                    them into clear and compelling messages, and develop
                    communication strategies that resonate with diverse
                    audiences."
                  </p>
                </div>

                <div
                  className="surface p-6"
                  data-testid="reference-q-outcomes"
                >
                  <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                    Outcomes delivered
                  </div>
                  <p className="text-sm text-[var(--af-cream)]/85 italic leading-relaxed">
                    "Strengthened brand awareness, increased visitor
                    engagement, improved marketing effectiveness, and
                    navigated changing tourism trends with innovative and
                    thoughtful campaigns."
                  </p>
                </div>
              </div>

              <div className="surface p-6" data-testid="reference-attribution">
                <div className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-cream)]/55 mb-2">
                  Reference Provided By
                </div>
                <div className="font-display text-xl">Maureen Kelley</div>
                <div className="text-sm text-[var(--af-cream)]/75">
                  Director of Economic Development & Tourism
                </div>
                <div className="text-sm text-[var(--af-lime)] mt-0.5">
                  Nelson County, Virginia
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* GRID OF REST */}
      <section
        className="px-5 sm:px-8 pb-20 max-w-7xl mx-auto"
        data-testid="case-studies-grid-section"
      >
        <div className="mb-10">
          <div className="eyebrow mb-4">More Campaigns</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">
            More work.{" "}
            <span className="text-[var(--af-lime)]">More proof.</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-6">
          {rest.map((cs) => (
            <StoryCard key={cs.slug} cs={cs} />
          ))}
        </div>
      </section>

      {/* WHY THIS MATTERS */}
      <section
        className="border-t border-[var(--af-line)] bg-[var(--af-navy-deep)] px-5 sm:px-8 py-20"
        data-testid="case-studies-why-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Why It Works</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-5">
              The same playbook,
              <br />
              <span className="text-[var(--af-lime)]">tailored to you.</span>
            </h2>
            <p className="text-base text-[var(--af-cream)]/75 leading-relaxed">
              Every campaign above started where yours will: a one-page
              strategy, a clear KPI, and a budget under $10K to start. The
              difference is the campaign architecture, how the channels work
              together, how creative flexes by season, and how we measure what
              actually matters.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { icon: TrendingUp, t: "Measured against benchmarks", d: "Every campaign reports against industry benchmarks, and most outperform by double digits." },
              { icon: Sparkles, t: "Creative concepting included", d: "Wanderlove. Eat. Drink. Love. Dark Skies. We build the concept and place the media." },
              { icon: Globe, t: "Multi-state by design", d: "We routinely run campaigns across DC, Richmond, Charlotte, Raleigh, and beyond." },
              { icon: Quote, t: "Client retention speaks loudest", d: "Nelson County is in their 4th+ year with us. That's the work compounding." },
            ].map((d, i) => (
              <div key={i} className="surface p-5">
                <d.icon size={20} className="text-[var(--af-lime)] mb-3" />
                <div className="font-display text-lg mb-1.5">{d.t}</div>
                <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                  {d.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SampleAdsCTASection
        label="Tourism"
        audience="tourism"
        testIdPrefix="case-studies-sample-ads"
      />

      {/* LEAD FORM */}
      <section
        id="lead-form"
        className="grid-bg px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
        data-testid="lead-form-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="eyebrow mb-4">Run a Campaign Like These</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6">
              Want results
              <br />
              <span className="text-[var(--af-lime)]">like the ones above?</span>
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
              Tell us about your destination, your KPI, and your timeline.
              We'll come back with a tailored campaign concept, for free.
            </p>
            <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Free 20-minute strategy call
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Campaign-concept direction included
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                We'll benchmark your numbers against ours
              </li>
            </ul>
          </div>
          <LeadForm
            trade="tourism-case-studies"
            tradeName="Tourism"
          />
        </div>
      </section>
    </SiteShell>
  );
};

export default CaseStudies;
